import { ROUTE_PATHS } from '@/shared/constants/routes';
import type { LucideIcon } from 'lucide-react';
import type { MenuGroup, MenuLink, SidebarMenuItem } from './sidebarMenuConfig';

export type BreadcrumbSegment = {
  /** 없으면 클릭 불가(메뉴 그룹 등) */
  path?: string;
  label: string;
  icon?: LucideIcon;
};

/** 메뉴에 없는 상세·서브 화면 → 상위 목록 메뉴 매핑 */
const DETAIL_ROUTE_PARENT: Partial<Record<string, string>> = {
  [ROUTE_PATHS.DEMO_BATCH_JOB_DETAIL]: ROUTE_PATHS.DEMO_BATCH_JOBS,
  // 사이드바 없이 의약품 목록 버튼으로만 진입
  [ROUTE_PATHS.DRUG_DATA_CREATE]: ROUTE_PATHS.DRUGS,
};

/** 상세 기본 라벨('상세') 대신 쓸 서브 화면 이름 */
const DETAIL_ROUTE_LABEL: Partial<Record<string, string>> = {
  [ROUTE_PATHS.DRUG_DATA_CREATE]: '의약품 정보 생성',
  [ROUTE_PATHS.PARTNER_COMPANY_NEW]: '업체 등록',
};

type MenuEntry = {
  link: MenuLink;
  groups: MenuGroup[];
};

const collectMenuEntries = (
  items: SidebarMenuItem[],
  groups: MenuGroup[] = [],
): MenuEntry[] => {
  const entries: MenuEntry[] = [];

  for (const item of items) {
    if ('type' in item) continue;

    if ('children' in item) {
      entries.push(...collectMenuEntries(item.children, [...groups, item]));
      continue;
    }

    if ('key' in item) {
      entries.push({ link: item, groups });
    }
  }

  return entries;
};

const findMenuEntry = (
  items: SidebarMenuItem[],
  pathname: string,
): MenuEntry | null => {
  const entries = collectMenuEntries(items);

  const exact = entries.find((entry) => entry.link.key === pathname);
  if (exact) return exact;

  const parentKey = DETAIL_ROUTE_PARENT[pathname];
  if (parentKey) {
    const parent = entries.find((entry) => entry.link.key === parentKey);
    if (parent) return parent;
  }

  const prefixMatches = entries
    .filter((entry) => pathname.startsWith(`${entry.link.key}/`))
    .sort((a, b) => b.link.key.length - a.link.key.length);

  return prefixMatches[0] ?? null;
};

const isDetailRoute = (pathname: string, listPath: string) =>
  pathname !== listPath &&
  (pathname.startsWith(`${listPath}/`) ||
    DETAIL_ROUTE_PARENT[pathname] === listPath);

/**
 * 사이드바 메뉴 구조를 반영한 브레드크럼 생성.
 * 하위 메뉴: 홈 → 그룹 → 페이지 (최소 3단계)
 * 상세 화면: … → 상세 (4단계)
 */
export const buildMenuBreadcrumbs = (
  root: BreadcrumbSegment,
  items: SidebarMenuItem[],
  pathname: string,
): BreadcrumbSegment[] => {
  const segments: BreadcrumbSegment[] = [root];
  const entry = findMenuEntry(items, pathname);

  if (!entry) {
    return segments;
  }

  for (const group of entry.groups) {
    segments.push({
      label: group.label,
      icon: group.icon,
    });
  }

  segments.push({
    path: entry.link.key,
    label: entry.link.label,
    icon: entry.link.icon,
  });

  if (isDetailRoute(pathname, entry.link.key)) {
    segments.push({
      label: DETAIL_ROUTE_LABEL[pathname] ?? '상세',
      icon: entry.link.icon,
    });
  }

  return segments;
};
